import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import { useApi, microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  AZURE_ARM_BASE_URL,
  AZURE_ARM_SCOPE,
  AZURE_MANAGEMENT_GROUP_API_VERSION,
  AZURE_SUBSCRIPTIONS_API_VERSION,
  LANDING_ZONE_MANAGEMENT_GROUP_ID,
} from '../constants';

type AzureSubscription = {
  subscriptionId: string;
  displayName: string;
  state: string;
};

type AzureManagementGroupDescendant = {
  name: string;
  id?: string;
  type?: string;
};

const UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const getDescendantSubscriptionId = (
  descendant: AzureManagementGroupDescendant,
) => {
  if (UUID_PATTERN.test(descendant.name)) {
    return descendant.name;
  }

  const idMatch = descendant.id?.match(
    /\/subscriptions\/([0-9a-fA-F-]{36})(?:\/|$)/,
  );
  return idMatch?.[1];
};

const isSubscriptionDescendant = (
  descendant: AzureManagementGroupDescendant,
) => descendant.type?.toLowerCase().endsWith('/subscriptions') ?? false;

export const AzureSubscriptionPickerField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const [subscriptions, setSubscriptions] = useState<AzureSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [manualMode, setManualMode] = useState(false);

  const loadSubscriptions = async () => {
    setLoading(true);
    setError(undefined);

    try {
      const token = await microsoftAuth.getAccessToken(AZURE_ARM_SCOPE);
      const headers = { Authorization: `Bearer ${token}` };

      const [descendantsResponse, subscriptionsResponse] = await Promise.all([
        fetch(
          `${AZURE_ARM_BASE_URL}/providers/Microsoft.Management/managementGroups/${LANDING_ZONE_MANAGEMENT_GROUP_ID}/descendants?api-version=${AZURE_MANAGEMENT_GROUP_API_VERSION}`,
          { headers },
        ),
        fetch(
          `${AZURE_ARM_BASE_URL}/subscriptions?api-version=${AZURE_SUBSCRIPTIONS_API_VERSION}`,
          { headers },
        ),
      ]);

      if (!descendantsResponse.ok) {
        const body = await descendantsResponse.text();
        throw new Error(
          `ARM API returned ${descendantsResponse.status}${body ? `: ${body}` : ''}`,
        );
      }

      if (!subscriptionsResponse.ok) {
        const body = await subscriptionsResponse.text();
        throw new Error(
          `ARM API returned ${subscriptionsResponse.status}${
            body ? `: ${body}` : ''
          }`,
        );
      }

      const descendantsData = (await descendantsResponse.json()) as {
        value: AzureManagementGroupDescendant[];
      };
      const subscriptionsData = (await subscriptionsResponse.json()) as {
        value: AzureSubscription[];
      };
      const landingZoneSubscriptionIds = new Set(
        descendantsData.value
          .filter(isSubscriptionDescendant)
          .map(getDescendantSubscriptionId)
          .filter((subscriptionId): subscriptionId is string =>
            Boolean(subscriptionId),
          ),
      );
      const enabled = subscriptionsData.value
        .filter(sub => landingZoneSubscriptionIds.has(sub.subscriptionId))
        .filter(sub => sub.state === 'Enabled');

      enabled.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setSubscriptions(enabled);
      setManualMode(false);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unknown error');
      }
      setManualMode(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microsoftAuth]);

  const hasFormErrors = !!rawErrors?.length;
  const title = schema.title ?? 'Azure Subscription';

  if (loading) {
    return (
      <FormControl fullWidth>
        <InputLabel shrink>{title}</InputLabel>
        <CircularProgress size={24} style={{ marginTop: 16 }} />
      </FormControl>
    );
  }

  if (manualMode) {
    const value = formData ?? '';
    const invalid = value !== '' && !UUID_PATTERN.test(value);
    return (
      <FormControl fullWidth required={required} error={hasFormErrors || invalid}>
        <TextField
          label={title}
          required={required}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          error={hasFormErrors || invalid}
          InputLabelProps={{ shrink: true }}
        />
        <FormHelperText>
          {error
            ? `Could not load subscriptions automatically (${error}). Enter the subscription UUID manually — find it in the Azure portal under "Subscriptions".`
            : 'Enter the Azure subscription UUID.'}
          {' '}
          <Link component="button" type="button" onClick={loadSubscriptions}>
            Retry
          </Link>
        </FormHelperText>
        {invalid && (
          <FormHelperText error>
            Must be a UUID (8-4-4-4-12 hex format).
          </FormHelperText>
        )}
        {hasFormErrors && <FormHelperText error>{rawErrors![0]}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth required={required} error={hasFormErrors}>
      <InputLabel shrink id="azure-subscription-label">
        {title}
      </InputLabel>
      <Select
        labelId="azure-subscription-label"
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
        displayEmpty
      >
        <MenuItem value="" disabled>
          — select a subscription —
        </MenuItem>
        {subscriptions.map(sub => (
          <MenuItem key={sub.subscriptionId} value={sub.subscriptionId}>
            {sub.displayName} ({sub.subscriptionId})
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {schema.description ?? ''}{' '}
        <Link
          component="button"
          type="button"
          onClick={() => {
            setManualMode(true);
            onChange('');
          }}
        >
          Enter manually instead
        </Link>
      </FormHelperText>
      {hasFormErrors && <FormHelperText error>{rawErrors![0]}</FormHelperText>}
    </FormControl>
  );
};
